import React from 'react';
import cs from 'classnames';
import {connect} from 'react-redux';
import GoogleMapReact from 'google-map-react';
import get from 'lodash/get';
import differenceInHours from 'date-fns/difference_in_hours';

import findZoomAndCenter from '~/misc/findZoomAndCenter';
import Polyline from './Polyline';
import WeightStationIcon from './weightStation.svg';
import TruckStopIcon from './truckStop.svg';
import Point from '~/components/common/DirectionPoint';

import css from './index.css';


const PoiMarker = connect()(() => <div className={cs(css.marker, css.poiMarker)}/>);

const truckStopStatus = {
  3: 'full',
  2: 'some',
  1: 'free',
};

const TruckStopMarker = connect()(({status, updatedAt, rating}) => {
  const isFresh = differenceInHours(updatedAt, new Date()) <= 24;
  const statusValue = truckStopStatus[status];
  const good = isFresh && statusValue === 'free';
  const bad = isFresh && statusValue === 'full';
  const medium = isFresh && statusValue === 'some';

  return (
    <div className={css.loadedMarker}>
      <div className={cs(css.truckStopMarker, {
        [css.goodPoint]: good,
        [css.badPoint]: bad,
        [css.mediumPoint]: medium,
      })}
      >
        <TruckStopIcon/>
      </div>
      <div className={css.markerInfo}>
        Truckstop <br/>
        {}
        {isFresh
          ? (
            <span className={cs({
              [css.goodStatus]: good,
              [css.mediumStatus]: medium,
              [css.badStatus]: bad,
            })}
            >{statusValue}</span>
          )
          : 'status — outdated'
        }
        <br/>
        {(rating && parseFloat(rating)) ? `rating is ${parseFloat(rating).toFixed(1)}/5` : null}
      </div>


    </div>
  );
});

const weightStationStatus = {
  0: 'closed',
  1: 'open',
};

const WeightStationMarker = connect()(({status, updatedAt}) => {
  const isFresh = differenceInHours(updatedAt, new Date()) <= 24;
  const statusValue = weightStationStatus[status];
  const good = isFresh && statusValue === 'closed';
  const bad = isFresh && statusValue === 'open';

  return (
    <div className={css.loadedMarker}>

      <div className={cs(css.weightStationMarker, {
        [css.goodPoint]: good,
        [css.badPoint]: bad,
      })}
      >
        <WeightStationIcon/>
      </div>
      <div className={css.markerInfo}>
        Weight Station <br/>
        {isFresh
          ? (
            <span className={cs({
              [css.goodStatus]: good,
              [css.badStatus]: bad,
            })}
            >{statusValue}</span>
          )
          : 'status — outdated'
        }
        <br/>
      </div>
    </div>
  );
});

class LoadMap extends React.Component {
  render() {
    const {
      width,
      height,
      path,
      allowControlls,
      origin,
      destination,
      poiLocations,
      weightStations,
      truckStops,
      toggledTruckStops,
      toggledWeightStations,
    } = this.props;

    if (!origin || !destination) {
      return null;
    }

    let mapPath = path&&path.length? path : [origin, destination];

    const {center, zoom} = mapPath ? findZoomAndCenter({
      size: {width, height},
      defaultCenter: {lat: 38.9217257, lng: -97.9265935},
      zoomBounds: {min: 1, max: 10},
    }, mapPath) : {
      zoom: 3,
      center: {lat: 38.9217257, lng: -97.9265935},
    };

    const showDraftPoi = (toggledTruckStops || toggledWeightStations)
      && !weightStations && !truckStops && poiLocations;

    const showTruckStops = (toggledTruckStops && truckStops);
    const showWeightStations = (toggledWeightStations && weightStations);

    return (
      <GoogleMapReact
        center={center}
        zoom={zoom}
        options={({
          draggable: Boolean(allowControlls),
          zoomControl: Boolean(allowControlls),
          fullscreenControl: false,
          scrollwheel: Boolean(allowControlls),
          disableDoubleClickZoom: !allowControlls,
          gestureHandling: !allowControlls && 'none',
        })}
        onGoogleApiLoaded={({map, maps}) => {
          this.setState({map: map, maps: maps, mapLoaded: true});
        }}
        yesIWantToUseGoogleMapApiInternals={true}
      >
        {showDraftPoi && poiLocations.map(({id, location}) => (<PoiMarker
          key={id}
          lat={location[1]}
          lng={location[0]}
        />))}
        {showTruckStops && truckStops.map((item) => (<TruckStopMarker
          key={item.id}
          lat={item.location[1]}
          lng={item.location[0]}

          status={get(item, 'truck_stop.status')}
          updatedAt={get(item, 'truck_stop.status_updated_at')}
          rating={item.rating}
        />))}
        {showWeightStations && weightStations.map((item) => (<WeightStationMarker
          key={item.id}
          lat={item.location[1]}
          lng={item.location[0]}

          status={get(item, 'status')}
          updatedAt={get(item, 'status_updated_at')}
        />))}

        {this.state && this.state.mapLoaded && (mapPath || (origin && destination)) && (
          <Polyline
            color='rgba(94, 102, 185, 0.78)'
            map={this.state.map}
            maps={this.state.maps}
            path={mapPath}
            origin={origin}
            destination={destination}
          />
        )}

        {destination && (
          <Point
            onMap={true}
            lat={destination.lat}
            lng={destination.lng}
            title='Destination'
            text={'B'}
          />
        )}

        {origin && (
          <Point
            onMap={true}
            isOrigin={true}
            lat={origin.lat}
            lng={origin.lng}
            title='Origin'
            text={'A'}
          />
        )}
      </GoogleMapReact>
    );
  }
}

export default connect(({loads: {maps, mapData, toggledTruckStops, toggledWeightStations}}, {loadId}) => {
  const {origin, destination} = get(mapData[loadId], 'points') || {};
  return {
    toggledTruckStops: toggledTruckStops[loadId] || null,
    toggledWeightStations: toggledWeightStations[loadId] || null,
    origin,
    destination,
    path: get(mapData[loadId], 'path'),
    poiLocations: get(mapData[loadId], 'poiLocations'),
    weightStations: get(mapData[loadId], 'weightStations'),
    truckStops: get(mapData[loadId], 'truckStops'),
  };
})(LoadMap);



// WEBPACK FOOTER //
// ./src/components/LoadMap/index.js